import React, { useState, useEffect } from "react";

const SplashScreen = ({ onFinish }) => {
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(0); // 0: assembling, 1: assembled, 2: text, 3: fade out
  const [hidden, setHidden] = useState(false);
  // Fast mode for in-session navigation (already seen splash once)
  const fast = typeof window !== "undefined" && sessionStorage.getItem("arabell_splash_seen") === "1";

  useEffect(() => {
    const factor = fast ? 0.5 : 1;
    const t1 = setTimeout(() => setStage(1), Math.round(1850 * factor));
    const t2 = setTimeout(() => setStage(2), Math.round(2400 * factor));

    const start = Date.now();
    const duration = Math.round(4100 * factor);
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min(100, Math.round((elapsed / duration) * 100));
      setProgress(p);
      if (p >= 100) clearInterval(interval);
    }, 30);

    const t3 = setTimeout(() => setStage(3), Math.round(4300 * factor));
    const t4 = setTimeout(() => {
      setHidden(true);
      try { sessionStorage.setItem("arabell_splash_seen", "1"); } catch {}
      onFinish && onFinish();
    }, Math.round(5000 * factor));

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearInterval(interval);
    };
  }, [onFinish]);

  if (hidden) return null;

  const logo = "/arabell-logo-clean.png";

  // Each "piece" reveals a region of the logo, animated into place from a different direction.
  const pieces = [
    {
      // Left bar of the triangle (top-left region)
      clip: "polygon(0% 0%, 50% 0%, 50% 75%, 30% 75%, 0% 75%)",
      keyframe: "flyLeft",
      delay: "0ms",
    },
    {
      // Right bar of the triangle (top-right region)
      clip: "polygon(50% 0%, 100% 0%, 100% 75%, 70% 75%, 50% 75%)",
      keyframe: "flyRight",
      delay: "200ms",
    },
    {
      // Bottom band + the ARABELL GROUP text
      clip: "polygon(0% 60%, 100% 60%, 100% 100%, 0% 100%)",
      keyframe: "flyBottom",
      delay: "400ms",
    },
  ];

  return (
    <div
      className={`fixed inset-0 z-[300] bg-black flex flex-col items-center justify-center overflow-hidden transition-opacity duration-1000 ease-out ${
        stage === 3 ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
    >
      <style>{`
        @keyframes flyLeft {
          0%   { transform: translate(-340px, -120px) rotate(-22deg) scale(1.05); opacity: 0; filter: blur(10px); }
          35%  { opacity: 0.85; }
          100% { transform: translate(0, 0) rotate(0) scale(1); opacity: 1; filter: blur(0); }
        }
        @keyframes flyRight {
          0%   { transform: translate(340px, -120px) rotate(22deg) scale(1.05); opacity: 0; filter: blur(10px); }
          35%  { opacity: 0.85; }
          100% { transform: translate(0, 0) rotate(0) scale(1); opacity: 1; filter: blur(0); }
        }
        @keyframes flyBottom {
          0%   { transform: translate(0, 280px) scale(1.05); opacity: 0; filter: blur(10px); }
          35%  { opacity: 0.85; }
          100% { transform: translate(0, 0) scale(1); opacity: 1; filter: blur(0); }
        }
        @keyframes flarePulse {
          0%   { opacity: 0; transform: scale(0.6); }
          40%  { opacity: 0.9; transform: scale(1.5); }
          100% { opacity: 0.25; transform: scale(1.3); }
        }
        @keyframes scanLine {
          0%   { transform: translateY(-100%); opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(100%); opacity: 0; }
        }
      `}</style>

      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(201,169,97,0.06) 0%, rgba(0,0,0,0) 60%)",
        }}
      />

      {/* Top label */}
      <div className="absolute top-10 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.5em] uppercase text-white/30">
        Arabell Group
      </div>

      {/* Logo composition area */}
      <div className="relative w-[280px] h-[280px] md:w-[380px] md:h-[380px] flex items-center justify-center">
        {/* Animated pieces */}
        {pieces.map((p, i) => (
          <div
            key={i}
            className="absolute inset-0"
            style={{
              animation: `${p.keyframe} 2400ms cubic-bezier(0.22, 1, 0.36, 1) ${p.delay} both`,
            }}
          >
            <img
              src={logo}
              alt=""
              className="absolute inset-0 w-full h-full object-contain"
              style={{
                filter: "invert(1) brightness(1.1)",
                clipPath: p.clip,
                WebkitClipPath: p.clip,
              }}
              draggable={false}
            />
          </div>
        ))}

        {/* Scan line during assembly */}
        {stage < 1 && (
          <div
            className="absolute inset-x-0 h-12 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(201,169,97,0) 0%, rgba(201,169,97,0.6) 50%, rgba(201,169,97,0) 100%)",
              animation: "scanLine 2400ms linear forwards",
            }}
          />
        )}

        {/* Glow flare on assembly */}
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(201,169,97,0.55) 0%, rgba(201,169,97,0) 60%)",
            animation:
              stage >= 1 ? "flarePulse 1800ms ease-out forwards" : "none",
            opacity: 0,
          }}
        />

        {/* Final crisp logo on top */}
        <img
          src={logo}
          alt="Arabell Group"
          className="absolute inset-0 w-full h-full object-contain"
          style={{
            filter: "invert(1) brightness(1.1)",
            opacity: stage >= 1 ? 1 : 0,
            transform: stage >= 1 ? "scale(1)" : "scale(0.96)",
            transition:
              "opacity 1000ms ease-out 200ms, transform 1300ms cubic-bezier(0.22,1,0.36,1) 200ms",
          }}
          draggable={false}
        />
      </div>

      {/* Tagline */}
      <div
        className="mt-10 text-center"
        style={{
          opacity: stage >= 2 ? 1 : 0,
          transform: stage >= 2 ? "translateY(0)" : "translateY(12px)",
          transition: "opacity 1000ms ease-out, transform 1100ms ease-out",
        }}
      >
        <p className="text-[10px] md:text-xs tracking-[0.6em] uppercase text-arabell-gold">
          Move the World
        </p>
      </div>

      {/* Loading bar */}
      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 w-[260px] md:w-[380px]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] tracking-[0.4em] uppercase text-white/50">
            Loading
          </span>
          <span className="text-[10px] tracking-[0.3em] text-arabell-gold tabular-nums">
            {String(progress).padStart(3, "0")} / 100
          </span>
        </div>
        <div className="relative h-px w-full bg-white/15 overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full bg-arabell-gold"
            style={{
              width: `${progress}%`,
              transition: "width 100ms linear",
              boxShadow: "0 0 12px rgba(201,169,97,0.7)",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
